import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Category } from '../types';
import { categoryAPI } from '../services/api';
import './CategoryManagement.css';

const CategoryManagement: React.FC = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [newCategory, setNewCategory] = useState({
    name: '',
    type: 'expense' as 'income' | 'expense',
    settlementFlag: null as number | null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const categoriesData = await categoryAPI.getAll();
      setCategories(categoriesData);
    } catch (err) {
      console.error('カテゴリ取得エラー:', err);
      setError(t('category:categoryLoadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewCategory(prev => ({
      ...prev,
      [name]: value,
      // Reset settlementFlag when changing type
      ...(name === 'type' && { settlementFlag: null })
    }));
  };

  const handleSettlementFlagChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewCategory(prev => ({
      ...prev,
      settlementFlag: e.target.checked ? 1 : null
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newCategory.name.trim()) {
      setError(t('category:categoryNameRequired'));
      return;
    }

    // Check for duplicate category names
    const isDuplicate = categories.some(cat => 
      cat.name.toLowerCase() === newCategory.name.trim().toLowerCase() && 
      cat.type === newCategory.type
    );

    if (isDuplicate) {
      setError(t('category:categoryAlreadyExists'));
      return;
    }

    // Check for duplicate settlement flag
    if (newCategory.settlementFlag === 1) {
      const hasSettlementFlag = categories.some(cat => 
        cat.type === 'income' && cat.settlementFlag === 1
      );
      if (hasSettlementFlag) {
        setError(t('category:settlementFlagAlreadyExists'));
        return;
      }
    }

    setIsSubmitting(true);
    setError('');

    try {
      const result = await categoryAPI.create({
        name: newCategory.name.trim(),
        type: newCategory.type,
        settlementFlag: newCategory.settlementFlag
      });

      const createdCategory: Category = {
        id: result.id,
        name: newCategory.name.trim(),
        type: newCategory.type,
        settlementFlag: newCategory.settlementFlag,
        createdAt: new Date().toISOString()
      };

      setCategories(prev => [...prev, createdCategory]);
      setNewCategory({ name: '', type: 'expense', settlementFlag: null });
      setSuccessMessage(t('category:categoryCreated'));
    } catch (err) {
      console.error('カテゴリ作成エラー:', err);
      setError(t('category:categoryCreateError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('category:confirmDelete'))) {
      return;
    }

    try {
      await categoryAPI.delete(id);
      setCategories(prev => prev.filter(cat => cat.id !== id));
      setSuccessMessage(t('category:categoryDeleted'));
    } catch (err) {
      console.error('カテゴリ削除エラー:', err);
      setError(t('category:categoryDeleteError'));
    }
  };

  const clearMessages = () => {
    setError('');
    setSuccessMessage('');
  };

  if (loading) return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '200px',
      fontSize: '18px',
      color: '#6b7280'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <div style={{
          width: '20px',
          height: '20px',
          border: '3px solid #e5e7eb',
          borderTop: '3px solid #3b82f6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        {t('common:loading')}
      </div>
    </div>
  );

  return (
    <div className="category-management">
      <h2>{t('category:categoryManagement')}</h2>
      
      {error && (
        <div className="message error">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="message success">
          {successMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className="category-form">
        <div className="form-group">
          <label htmlFor="name">{t('category:categoryName')} *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={newCategory.name}
            onChange={handleInputChange}
            placeholder={t('category:enterCategoryName')}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="type">{t('common:type')} *</label>
          <select
            id="type"
            name="type"
            value={newCategory.type}
            onChange={handleInputChange}
            required
          >
            <option value="expense">{t('transaction:expense')}</option>
            <option value="income">{t('transaction:income')}</option>
          </select>
        </div>

        {newCategory.type === 'income' && (
          <div className="form-group">
            <label className="checkbox-label">
              <span className="checkbox-label-text">{t('transaction:settlementFlag')}</span>
              <input
                type="checkbox"
                checked={newCategory.settlementFlag === 1}
                onChange={handleSettlementFlagChange}
              />
            </label>
          </div>
        )}

        <button 
          type="submit" 
          disabled={isSubmitting}
          className="btn btn-primary"
        >
          {isSubmitting ? t('category:creating') : t('common:add')}
        </button>
      </form>

      <div className="category-list">
        <h3>{t('category:existingCategories')}</h3>
        
        {categories.length === 0 ? (
          <div className="empty-state">
            <p>{t('category:noCategories')}</p>
          </div>
        ) : (
          <div className="categories-grid">
            {categories.map((category) => (
              <div key={category.id} className="category-item">
                <div className="category-info">
                  <span className="category-name">{category.name}</span>
                  <div className="category-badges">
                    <span className={`category-type ${category.type}`}>
                      {category.type === 'income' ? t('transaction:income') : t('transaction:expense')}
                    </span>
                    {category.settlementFlag === 1 && (
                      <span className="settlement-flag">
                        {t('transaction:settlementFlag')}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(category.id)}
                  className="btn btn-danger"
                  title={t('common:delete')}
                >
                  {t('common:delete')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryManagement;